import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  IconButton,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  MenuItem,
  Collapse,
} from '@mui/material';
import { ArrowBack, Save, Add, Delete } from '@mui/icons-material';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { editEventSchema } from '@utils/validators';
import { useAuth } from '@hooks/useAuth';
import { getEventById, updateEvent, getEventsBySeries, createEvent, deleteEvent } from '@services/eventService';
import { Timestamp } from 'firebase/firestore';

const EditEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { userData: user } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingEvent, setFetchingEvent] = useState(true);
  const [event, setEvent] = useState(null);
  const [editScope, setEditScope] = useState('single');

  // Recurrence edit state (separate from react-hook-form to avoid schema conflicts)
  const [editPattern, setEditPattern] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editWeekNumber, setEditWeekNumber] = useState('');
  const [editDayOfWeek, setEditDayOfWeek] = useState('');
  const [seriesStartDate, setSeriesStartDate] = useState('');
  const [loadingSeriesInfo, setLoadingSeriesInfo] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(editEventSchema),
    defaultValues: {
      title: '',
      description: '',
      location: '',
      date: '',
      startTime: '',
      endTime: '',
      volunteersNeeded: 1,
      assignments: [
        { name: '', description: '', volunteersNeeded: 1 }
      ],
      isRecurring: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'assignments',
  });

  // Fetch event data on mount
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setFetchingEvent(true);
        setError('');
        const eventData = await getEventById(eventId);
        setEvent(eventData);

        // Format date for input (YYYY-MM-DD)
        const eventDate = eventData.date?.toDate();
        const formattedDate = eventDate
          ? eventDate.toISOString().split('T')[0]
          : '';

        // Reset form with fetched data
        reset({
          title: eventData.title || '',
          description: eventData.description || '',
          location: eventData.location || '',
          date: formattedDate,
          startTime: eventData.startTime || '',
          endTime: eventData.endTime || '',
          volunteersNeeded: eventData.volunteersNeeded || 1,
          assignments: eventData.assignments?.length > 0
            ? eventData.assignments.map(a => ({
                name: a.name || '',
                description: a.description || '',
                volunteersNeeded: a.volunteersNeeded || 1,
                startTime: a.startTime || '',
                endTime: a.endTime || '',
              }))
            : [{ name: '', description: '', volunteersNeeded: 1 }],
          isRecurring: false,
        });
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event details. Please try again.');
      } finally {
        setFetchingEvent(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId, reset]);

  // Load series info when admin switches to series edit scope
  useEffect(() => {
    if (editScope !== 'series' || !event?.isRecurring || !event?.recurrenceId) return;
    const load = async () => {
      setLoadingSeriesInfo(true);
      try {
        const seriesEvs = await getEventsBySeries(event.recurrenceId, event.organizationId);
        if (seriesEvs.length > 0) {
          const toStr = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          setSeriesStartDate(toStr(seriesEvs[0].date.toDate()));
          setEditEndDate(toStr(seriesEvs[seriesEvs.length - 1].date.toDate()));
        }
        setEditPattern(event.recurrencePattern || '');
        setEditWeekNumber(event.monthlyWeekNumber || '');
        setEditDayOfWeek(event.monthlyDayOfWeek || '');
      } catch (err) {
        console.error('Error loading series info:', err);
      } finally {
        setLoadingSeriesInfo(false);
      }
    };
    load();
  }, [editScope, event?.recurrenceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Parse a YYYY-MM-DD string or Date object as a local date (avoids UTC-midnight timezone shift)
  const parseLocalDate = (dateInput) => {
    if (dateInput instanceof Date) return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
    const [year, month, day] = dateInput.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getNthWeekdayOfMonth = (year, month, weekNumber, dayOfWeek) => {
    const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    const targetDay = dayMap[dayOfWeek];
    if (weekNumber === 'last') {
      const lastDay = new Date(year, month + 1, 0);
      while (lastDay.getDay() !== targetDay) lastDay.setDate(lastDay.getDate() - 1);
      return lastDay;
    }
    const weekMap = { first: 1, second: 2, third: 3, fourth: 4 };
    const occurrence = weekMap[weekNumber];
    let count = 0;
    for (let day = 1; day <= 31; day++) {
      const date = new Date(year, month, day);
      if (date.getMonth() !== month) break;
      if (date.getDay() === targetDay && ++count === occurrence) return date;
    }
    return null;
  };

  const generateRecurringDates = (startDate, pattern, endDate, weekNumber, dayOfWeek) => {
    const dates = [];
    let current = parseLocalDate(startDate);
    const final = parseLocalDate(endDate);
    dates.push(new Date(current));
    while (current < final) {
      switch (pattern) {
        case 'weekly':    current.setDate(current.getDate() + 7); break;
        case 'biweekly':  current.setDate(current.getDate() + 14); break;
        case 'monthly':   current.setMonth(current.getMonth() + 1); break;
        case 'monthly-weekday': {
          let nm = current.getMonth() + 1, ny = current.getFullYear();
          if (nm > 11) { nm = 0; ny++; }
          current = getNthWeekdayOfMonth(ny, nm, weekNumber, dayOfWeek);
          if (!current) return dates;
          break;
        }
        default: return dates;
      }
      if (current <= final) dates.push(new Date(current));
    }
    return dates;
  };

  const onSubmit = async (data) => {
    try {
      console.log('Form submitted with data:', data);
      setError('');
      setLoading(true);

      // Parse date/time for this event
      const [startHours, startMinutes] = data.startTime.split(':');
      const eventDate = parseLocalDate(data.date);
      eventDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

      // Assignments for this event (preserve existing fill counts)
      const processedAssignments = data.assignments.map((assignment, index) => {
        const existingAssignment = event.assignments?.[index];
        return {
          name: assignment.name,
          description: assignment.description || '',
          volunteersNeeded: parseInt(assignment.volunteersNeeded),
          volunteersAssigned: existingAssignment?.volunteersAssigned || 0,
          startTime: assignment.startTime || null,
          endTime: assignment.endTime || null,
        };
      });

      if (editScope === 'series' && event?.recurrenceId) {
        const seriesEvents = await getEventsBySeries(event.recurrenceId, event.organizationId);
        const firstDate = seriesEvents[0].date.toDate();
        const [sh, sm] = data.startTime.split(':');

        // Determine effective recurrence settings (may have been edited)
        const pattern = editPattern || event.recurrencePattern;
        const endDate = editEndDate || toDateStr(seriesEvents[seriesEvents.length - 1].date.toDate());
        const weekNum = editWeekNumber || event.monthlyWeekNumber || '';
        const dayOfWk = editDayOfWeek || event.monthlyDayOfWeek || '';

        // Generate new date set
        const newDates = generateRecurringDates(toDateStr(firstDate), pattern, endDate, weekNum, dayOfWk);
        const newDateSet = new Set(newDates.map(d => toDateStr(d)));

        // Map existing events by date string
        const existingByDate = {};
        seriesEvents.forEach(ev => { existingByDate[toDateStr(ev.date.toDate())] = ev; });

        // Delete events no longer in new schedule
        for (const [dateStr, ev] of Object.entries(existingByDate)) {
          if (!newDateSet.has(dateStr)) await deleteEvent(ev.eventId);
        }

        // Create events for new dates not already present
        for (let i = 0; i < newDates.length; i++) {
          const dateStr = toDateStr(newDates[i]);
          if (!existingByDate[dateStr]) {
            const d = new Date(newDates[i]);
            d.setHours(parseInt(sh), parseInt(sm), 0, 0);
            await createEvent({
              title: data.title,
              description: data.description,
              location: data.location,
              date: Timestamp.fromDate(d),
              startTime: data.startTime,
              endTime: data.endTime,
              volunteersNeeded: parseInt(data.volunteersNeeded),
              assignments: processedAssignments.map(a => ({ ...a })),
              organizationId: event.organizationId,
              organizationName: event.organizationName,
              createdBy: event.createdBy || user.uid,
              isRecurring: true,
              recurrenceId: event.recurrenceId,
              recurrencePattern: pattern,
              monthlyWeekNumber: weekNum || null,
              monthlyDayOfWeek: dayOfWk || null,
              instanceNumber: i + 1,
              totalInstances: newDates.length,
            });
          }
        }

        // Re-fetch, update all events with shared fields + correct instance numbers
        const updatedSeries = await getEventsBySeries(event.recurrenceId, event.organizationId);
        const sharedFields = {
          title: data.title,
          description: data.description,
          location: data.location,
          startTime: data.startTime,
          endTime: data.endTime,
          volunteersNeeded: parseInt(data.volunteersNeeded),
          recurrencePattern: pattern,
          monthlyWeekNumber: weekNum || null,
          monthlyDayOfWeek: dayOfWk || null,
          totalInstances: newDates.length,
        };
        await Promise.all(updatedSeries.map((ev, idx) => {
          const assignments = data.assignments.map((a, ai) => ({
            name: a.name,
            description: a.description || '',
            volunteersNeeded: parseInt(a.volunteersNeeded),
            volunteersAssigned: ev.assignments?.[ai]?.volunteersAssigned || 0,
            startTime: a.startTime || null,
            endTime: a.endTime || null,
          }));
          return updateEvent(ev.eventId, { ...sharedFields, assignments, instanceNumber: idx + 1 });
        }));
      } else {
        await updateEvent(eventId, {
          title: data.title,
          description: data.description,
          location: data.location,
          date: Timestamp.fromDate(eventDate),
          startTime: data.startTime,
          endTime: data.endTime,
          volunteersNeeded: parseInt(data.volunteersNeeded),
          assignments: processedAssignments,
        });
      }

      navigate(`/events/${eventId}`);
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err.message || 'Failed to update event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has permission to edit
  const canEdit = user?.userType === 'org-admin' || user?.userType === 'platform-admin';

  if (fetchingEvent) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user || !canEdit) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You don't have permission to edit events.
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Event not found.
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/events/${eventId}`)}
          variant="outlined"
        >
          Back to Event
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Edit Event
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update the details below to modify this volunteer event
        </Typography>

        {event?.isRecurring && event?.recurrenceId && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              This is a recurring event (instance {event.instanceNumber} of {event.totalInstances}). What would you like to edit?
            </Typography>
            <RadioGroup row value={editScope} onChange={(e) => setEditScope(e.target.value)}>
              <FormControlLabel value="single" control={<Radio />} label="This event only" />
              <FormControlLabel value="series" control={<Radio />} label={`All ${event.totalInstances} events in series`} />
            </RadioGroup>
            {editScope === 'series' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Title, description, location, times, and assignments will be updated for all events. Each event keeps its own date.
              </Alert>
            )}

            {/* Recurrence settings — shown when editing the full series */}
            <Collapse in={editScope === 'series'} sx={{ mt: editScope === 'series' ? 2 : 0 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Recurrence Settings
              </Typography>
              {loadingSeriesInfo ? (
                <CircularProgress size={20} />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Repeat Pattern"
                      value={editPattern}
                      onChange={(e) => setEditPattern(e.target.value)}
                      disabled={loading}
                      size="small"
                    >
                      <MenuItem value="weekly">Weekly (Every week)</MenuItem>
                      <MenuItem value="biweekly">Biweekly (Every 2 weeks)</MenuItem>
                      <MenuItem value="monthly">Monthly (Same date each month)</MenuItem>
                      <MenuItem value="monthly-weekday">Monthly (Same weekday)</MenuItem>
                    </TextField>
                  </Grid>

                  {editPattern === 'monthly-weekday' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select fullWidth label="Which Week" size="small"
                          value={editWeekNumber} onChange={(e) => setEditWeekNumber(e.target.value)} disabled={loading}
                        >
                          {['first','second','third','fourth','last'].map(v => (
                            <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select fullWidth label="Day of Week" size="small"
                          value={editDayOfWeek} onChange={(e) => setEditDayOfWeek(e.target.value)} disabled={loading}
                        >
                          {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(v => (
                            <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Repeat Until"
                      type="date"
                      size="small"
                      value={editEndDate}
                      onChange={(e) => setEditEndDate(e.target.value)}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: seriesStartDate }}
                      helperText={seriesStartDate ? `Series starts ${seriesStartDate}` : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      Events will be added or removed to match the new schedule. Existing volunteer registrations are preserved where possible.
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Collapse>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
          setError('Please fix the form errors before submitting.');
        })} noValidate>
          {/* Event Name */}
          <TextField
            fullWidth
            label="Event Name"
            margin="normal"
            {...register('title')}
            error={!!errors.title}
            helperText={errors.title?.message}
            disabled={loading}
            placeholder="e.g., Community Beach Cleanup"
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={4}
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description?.message}
            disabled={loading}
            placeholder="Describe the event, what volunteers will be doing, and any important details..."
          />

          {/* Location */}
          <TextField
            fullWidth
            label="Location"
            margin="normal"
            {...register('location')}
            error={!!errors.location}
            helperText={errors.location?.message}
            disabled={loading}
            placeholder="e.g., Santa Monica Beach, 123 Ocean Ave"
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                {...register('date')}
                error={!!errors.date}
                helperText={errors.date?.message}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Volunteers Needed */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Volunteers Needed"
                type="number"
                {...register('volunteersNeeded')}
                error={!!errors.volunteersNeeded}
                helperText={errors.volunteersNeeded?.message}
                disabled={loading}
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* Start Time */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                {...register('startTime')}
                error={!!errors.startTime}
                helperText={errors.startTime?.message}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                {...register('endTime')}
                error={!!errors.endTime}
                helperText={errors.endTime?.message}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          {/* Assignments Section */}
          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Volunteer Assignments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage specific roles or tasks that volunteers can sign up for
                </Typography>
              </Box>
              <Button
                startIcon={<Add />}
                onClick={() => append({ name: '', description: '', volunteersNeeded: 1 })}
                disabled={loading}
                variant="outlined"
              >
                Add Assignment
              </Button>
            </Box>

            {errors.assignments && !Array.isArray(errors.assignments) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.assignments.message}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {fields.map((field, index) => (
                <Card key={field.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Assignment {index + 1}
                      </Typography>
                      {fields.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(index)}
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Assignment Name"
                          {...register(`assignments.${index}.name`)}
                          error={!!errors.assignments?.[index]?.name}
                          helperText={errors.assignments?.[index]?.name?.message}
                          disabled={loading}
                          placeholder="e.g., Setup Team, Registration Desk"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Volunteers Needed"
                          type="number"
                          {...register(`assignments.${index}.volunteersNeeded`)}
                          error={!!errors.assignments?.[index]?.volunteersNeeded}
                          helperText={errors.assignments?.[index]?.volunteersNeeded?.message}
                          disabled={loading}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description (Optional)"
                          multiline
                          rows={2}
                          {...register(`assignments.${index}.description`)}
                          error={!!errors.assignments?.[index]?.description}
                          helperText={errors.assignments?.[index]?.description?.message || 'Describe what this assignment involves'}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Assignment Start Time (Optional)"
                          type="time"
                          {...register(`assignments.${index}.startTime`)}
                          InputLabelProps={{ shrink: true }}
                          disabled={loading}
                          helperText="When this assignment starts within the event"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Assignment End Time (Optional)"
                          type="time"
                          {...register(`assignments.${index}.endTime`)}
                          InputLabelProps={{ shrink: true }}
                          disabled={loading}
                          helperText="When this assignment ends"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/events/${eventId}`)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            >
              {loading
                ? 'Saving Changes...'
                : editScope === 'series'
                  ? 'Save All in Series'
                  : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditEventPage;
